import { createContext, useContext, useEffect, useMemo } from 'react';

import { AccountInfo } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { WrapperProps } from '@reece/global-types';
import { noop } from 'lodash-es';
import { useLocation, useNavigate } from 'react-router-dom';
import useLocalStorage from 'use-local-storage';
import { Maybe } from 'yup';

import { loginRequest } from 'auth/msalConfig';

/**
 * Types
 */
export type AuthContextType = {
  /** True when a user is signed in via Entra ID (MSAL). */
  isAuthenticated: boolean;
  /** Active Entra ID account, when signed in. */
  account?: Maybe<AccountInfo>;
  /** Legacy email/password (DB-backed) session email. */
  email?: Maybe<string>;
  /** Legacy email/password (DB-backed) session id (X-Auth-Token). */
  sessionId?: Maybe<string>;
  setEmail: (email: Maybe<string>) => void;
  setSessionId: (sid: Maybe<string>) => void;
  /** Start the Entra ID sign-in redirect flow. */
  entraLogin: () => void;
  /** Sign out of Entra ID. */
  entraLogout: () => void;
};

/**
 * Default values
 */
export const defaultAuthContext: AuthContextType = {
  isAuthenticated: false,
  setSessionId: noop,
  setEmail: noop,
  entraLogin: noop,
  entraLogout: noop
};

/**
 * Context
 */
export const AuthContext = createContext(defaultAuthContext);
export const useAuthContext = () => useContext(AuthContext);

/**
 * Provider
 */
function AuthProvider({ children }: WrapperProps) {
  /**
   * Custom hooks
   */
  const [sessionId, setSessionId] = useLocalStorage<Maybe<string>>(
    'sessionId',
    null
  );
  const [email, setEmail] = useLocalStorage<Maybe<string>>(
    'sessionEmail',
    null
  );
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] ?? instance.getAllAccounts()[0] ?? null;
  // MSAL React can lag behind sessionStorage after redirect; trust cached accounts too.
  const hasEntraSession =
    isAuthenticated ||
    (inProgress === 'none' && instance.getAllAccounts().length > 0);

  /**
   * Memos
   */
  const providerValues = useMemo(
    () => ({
      isAuthenticated: hasEntraSession,
      account,
      email,
      sessionId,
      setEmail,
      setSessionId,
      entraLogin: () => {
        instance
          .loginRedirect({ ...loginRequest, prompt: 'select_account' })
          .catch((error) => {
            console.error('Entra ID login failed:', error);
          });
      },
      entraLogout: () => {
        setEmail(null);
        setSessionId(null);
        const activeAccount = account ?? instance.getAllAccounts()[0];
        instance.logoutRedirect({
          account: activeAccount,
          postLogoutRedirectUri: `${window.location.origin}/login`
        });
      }
    }),
    [
      hasEntraSession,
      account,
      email,
      sessionId,
      setEmail,
      setSessionId,
      instance,
      account
    ]
  );

  /**
   * Effects
   */
  // 🟡 Effect - Redirect to /login when neither Entra nor legacy session is present
  useEffect(() => {
    const isCallback = pathname === '/login/callback';
    if (
      pathname !== '/login' &&
      !isCallback &&
      !sessionId &&
      !hasEntraSession
    ) {
      setEmail(null);
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, sessionId, hasEntraSession]);

  // 🟡 Effect - Leave /login once Entra session is established (e.g. after redirect)
  useEffect(() => {
    if (pathname === '/login' && hasEntraSession && inProgress === 'none') {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, hasEntraSession, inProgress]);

  /**
   * Render
   */
  return (
    <AuthContext.Provider value={providerValues}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
